import { ChangeEvent, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useCreatePsiProfile } from '@/lib/api/psi-profile.ts';
import { useUploadProfilePhoto } from '@/lib/api/me.ts';
import PsiConfigFields, { PsiConfigForm } from './PsiConfigFields.tsx';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CircularProgress from '@mui/material/CircularProgress';

interface OnboardingPsiModalProps {
  open: boolean;
  onComplete: () => void;
}

export default function OnboardingPsiModal({ open, onComplete }: OnboardingPsiModalProps) {
  const [psiConfig, setPsiConfig] = useState<PsiConfigForm>({
    volume: null,
    totalClosedDeals: '',
    specialistInvestmentTypes: [],
    historicalAvgReturn: null,
    averageTicket: null,
    coveredProvinces: [],
    companyName: '',
    pricingModel: 'PACKAGE',
    serviceConfigs: [],
    feeAmount: null,
    feeReturnAmount: null,
    payoutInfo: { iban: '', bankName: '', accountHolderName: '' },
  });
  const { mutate: createProfile, isPending } = useCreatePsiProfile();

  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const {
    mutateAsync: uploadProfilePhoto,
    isPending: isUploadingPhoto,
    error: hasUploadingError,
  } = useUploadProfilePhoto();

  const handlePhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadProfilePhoto(file);
      setPhotoUrl(url);
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
    }
  };

  const handleSubmit = () => {
    createProfile(
      {
        volume: Number(psiConfig.volume),
        totalClosedDeals: Number(psiConfig.totalClosedDeals),
        specialistInvestmentTypes: psiConfig.specialistInvestmentTypes,
        historicalAvgReturn: Number(psiConfig.historicalAvgReturn),
        averageTicket: Number(psiConfig.averageTicket),
        preferredProvinces: psiConfig.coveredProvinces,
        companyName: psiConfig.companyName,
        pricingModel: psiConfig.pricingModel,
        serviceConfigs: psiConfig.serviceConfigs,
        feeAmount: Number(psiConfig.feeAmount),
        feeReturnAmount: Number(psiConfig.feeReturnAmount),
        payoutInfo: psiConfig.payoutInfo,
      },
      {
        onSuccess: () => onComplete(),
      }
    );
  };

  const isPsiValid =
    !!psiConfig.volume &&
    !!psiConfig.totalClosedDeals &&
    psiConfig.specialistInvestmentTypes.length > 0 &&
    !!psiConfig.historicalAvgReturn &&
    !!psiConfig.averageTicket;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Cuéntanos más sobre ti
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1} textAlign="center">
              Añade una foto de perfil. Esto ayudará a generar confianza entre los inversores y hará
              tu perfil más atractivo.
            </Typography>
            <Box position="relative" display="inline-block">
              <Avatar
                src={photoUrl || '/images/profile/default.png'}
                sx={{ width: 80, height: 80, mb: 1 }}
                alt="Foto de perfil"
              />
              <IconButton
                color="primary"
                aria-label="Subir foto de perfil"
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handlePhotoChange}
                  disabled={isUploadingPhoto}
                />
                {isUploadingPhoto ? <CircularProgress size={24} /> : <PhotoCamera />}
              </IconButton>
            </Box>
            {hasUploadingError && (
              <Typography color="error" variant="body2" mt={1}>
                Error al subir la foto. Inténtalo de nuevo.
              </Typography>
            )}
          </Box>
          <Typography variant="subtitle1" gutterBottom>
            Queremos conocer tus objetivos para ayudarte a encontrar los inversores que mejor
            encajen contigo y con tu estilo de inversión.
          </Typography>
          <PsiConfigFields value={psiConfig} onChange={setPsiConfig} includePayoutInfo={false} />
        </Box>
        <Box mt={3}>
          <Typography variant="caption" color="text.secondary">
            Solo usaremos esta información para personalizar tu experiencia en Inversor Hub.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isPsiValid || isPending}
        >
          Guardar y continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
